import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "../../api/axiosInstance";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { inventoryPresentation } from "../pages/inventoryIntelligenceMessages";
import { inventoryBasis, historicalExact, quantityFromExact, formatExactQuantity } from "../utils/inventoryUnitQuantity";
import InventoryQuantity from "./InventoryQuantity";
import usePurchasingReadAccess from "./usePurchasingReadAccess";
import EditDeliveryNotePopup from "./popups/EditDeliveryNotePopup";
import AddInventoryCountPopup from "./popups/AddInventoryCountPopup";
import { itemLedgerRequestParams, mergeItemLedgerPage } from "../utils/itemLedgerPagination";
import { buildDeliveryNoteUnitOptions } from "../utils/deliveryNoteUnits";
import "../PurchasingUi.css";
import "../pages/InventoryIntelligence.css";

const hasStockQuantity = (value) => Number.isFinite(Number(value)) && Math.abs(Number(value)) >= 0.00000001;

function convertLedgerNumber(value, factor) {
  if (value === null || value === undefined || String(value).trim() === "") return null;
  const result = Number(value) * factor;
  return Number.isFinite(result) ? result : null;
}

function getNumberToneClass(value) {
  const number = Number(value || 0);
  if (number > 0) return "sm-positive";
  if (number < 0) return "sm-negative";
  return "";
}

function isDirectConsumptionMovement(movement) {
  return (
    Boolean(movement?.is_direct_consumption_movement) ||
    movement?.movement_display_type === "direct_consumption" ||
    movement?.created_by === "system_direct_consumption" ||
    String(movement?.notes || "").toLowerCase().includes("automatic direct consumption")
  );
}

function getMovementTypeLabel(type, movement) {
  if (movement?.movement_display_type === "legacy_location_opening") return "Legacy Opening";
  if (movement?.movement_display_type === "delivery_note_correction") return "DN Correction";
  if (movement?.movement_display_type === "delivery_note_opening_adjustment") return "DN Correction";
  if (movement?.movement_display_type === "delivery_note_reversal") return "DN Reversal";
  if (isDirectConsumptionMovement(movement)) return "Direct Consumption";

  const labels = {
    purchase: "Purchase",
    return_to_supplier: "Supplier Return",
    issue_to_department: "Stock Dispatch",
    return_from_department: "Department Return",
    adjustment: "Regularization",
    initial_balance: "Initial Balance",
    count_difference: "Inventory Count",
    inventory_count: "Inventory Count",
  };

  if (labels[type]) return labels[type];
  return String(type || "Movement")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getLocationRoleLabel(movement, allEntries = [], t = (value) => value) {
  const role = String(movement?.location_role || "").trim();
  const labels = {
    source: "Source",
    destination: "Destination",
    location_impact: "Location impact",
    inventory_count: "Inventory count",
    legacy_opening: "Legacy opening",
    unallocated_impact: "Unallocated impact",
    transfer_source: "Transfer source",
    transfer_destination: "Transfer destination",
  };

  if (role === "transfer_source" || role === "transfer_destination") {
    const oppositeRole = role === "transfer_source" ? "transfer_destination" : "transfer_source";
    const counterpart =
      movement?.counterpart_location_name || movement?.counterpart_location_restricted
        ? null
        : allEntries.find(
            (entry) =>
              entry !== movement &&
              Number(entry?.movement_id || 0) === Number(movement?.movement_id || 0) &&
              entry?.location_role === oppositeRole
          );
    const counterpartName =
      movement?.counterpart_location_name ||
      counterpart?.location_name ||
      t(movement?.counterpart_location_restricted ? "restricted location" : "another location");
    return role === "transfer_source"
      ? `${t("Source")} → ${counterpartName}`
      : `${t("Destination")} ← ${counterpartName}`;
  }

  return t(labels[role] || String(role || "Location impact").replace(/_/g, " "));
}

function getMovementToneClass(movement) {
  const quantity = Number(movement?.quantity || 0);
  if (quantity > 0) return "sm-moveType--positive";
  if (quantity < 0) return "sm-moveType--negative";
  return "sm-moveType--neutral";
}

function getReferenceLabel(movement, t = (value) => value) {
  if (movement?.reference?.label) return movement.reference.label;
  if (movement?.reference_label) return movement.reference_label;

  const referenceId = movement?.reference_id;
  const referenceTable = movement?.reference_table;
  const labels = {
    inventory_entries: "Purchase entry",
    stock_requisition_lines: "Stock dispatch",
    inventory_counts: "Inventory count",
    inventory_revert: "Inventory revert",
    recipes: "Recipe",
  };

  if (!referenceTable && !referenceId) return "-";
  const label = t(labels[referenceTable] || String(referenceTable || "Reference").replace(/_/g, " "));
  return referenceId ? `${label} #${referenceId}` : label;
}

function getReferenceTarget(movement) {
  const target = movement?.reference?.target;
  const type = target?.type;
  const id = Number(target?.id);

  if (!type || !Number.isInteger(id) || id <= 0) return null;
  if (!["delivery_note", "inventory_count", "stock_return"].includes(type)) return null;

  return { type, id, business_unit_id: target.business_unit_id || movement.business_unit_id };
}

function truncateText(value, maxLength = 65) {
  const text = String(value || "-");
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`;
}

function InventoryItemMovementsModal({
  row,
  businessUnitId,
  ledger,
  loading,
  error,
  locationScope,
  onLocationScopeChange,
  onClose,
  onOpenReference,
  canOpenReference,
  onRetry,
  loadingMore,
  onLoadMore,
  displayUnitLevel,
  onDisplayUnitChange,
}) {
  const { languageId } = usePmsTheme();
  const { t, formatDate, formatDateTime, formatOptionalDecimal, formatOptionalCurrency, formatSignedDecimal, formatSignedCurrency, formatUnitCost } = useMemo(() => inventoryPresentation(languageId), [languageId]);
  const dialogRef = useRef(null);
  const ledgerMovements = ledger?.movements;
  const movements = useMemo(
    () => (Array.isArray(ledgerMovements) ? ledgerMovements : []),
    [ledgerMovements]
  );
  const locationLedger = ledger?.location_ledger || {};
  const locationEntries = useMemo(
    () => (Array.isArray(locationLedger.entries) ? locationLedger.entries : []),
    [locationLedger.entries]
  );
  const locationSummaries = useMemo(
    () => (Array.isArray(locationLedger.locations) ? locationLedger.locations : []),
    [locationLedger.locations]
  );
  const item = ledger?.item || {};
  const summary = ledger?.summary || {};
  const pagination = ledger?.pagination || {};
  const accessScope =
    locationLedger.access_scope || ledger?.access_scope || ledger?.location_access_scope || "all";
  const hasRestrictedLocationAccess =
    accessScope === "user_locations" || accessScope === "restricted" || accessScope === "scoped";
  const canViewLegacy =
    !hasRestrictedLocationAccess &&
    locationLedger.legacy_unallocated_visible !== false &&
    locationLedger.legacy_unallocated !== null;
  const globalTotalsVisible = locationLedger.global_totals_visible !== false;
  const globalScopeLabel = t(hasRestrictedLocationAccess ? "Authorized" : "Global");
  const showLegacyScope = canViewLegacy && hasStockQuantity(locationLedger.legacy_unallocated?.current_qty);
  const requestedLocation = locationSummaries.find((location) => location.scope_key === locationScope)
    || (row?.initialLocationSelection?.scope_key === locationScope ? row.initialLocationSelection : null);
  const isEmptyLocationScope = ledger && locationLedger.enabled && locationScope && locationScope !== "global"
    && (locationScope === "legacy_unallocated"
      ? !showLegacyScope
      : !hasStockQuantity(requestedLocation?.current_qty));
  const effectiveLocationScope =
    isEmptyLocationScope ||
    (locationScope === "legacy_unallocated" && !canViewLegacy) ||
    (ledger && locationLedger.enabled === false && locationScope !== "global")
      ? "global"
      : locationScope || "global";

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const dialog = dialogRef.current;
    dialog?.focus();
    const trapFocus = (event) => {
      if (event.key !== "Tab" || !dialog) return;
      const controls = [...dialog.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]')];
      const first = controls[0] || dialog;
      const last = controls[controls.length - 1] || dialog;
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog)) {
        event.preventDefault(); first.focus();
      }
    };
    dialog?.addEventListener("keydown", trapFocus);
    return () => { dialog?.removeEventListener("keydown", trapFocus); if (previouslyFocused?.isConnected) previouslyFocused.focus?.(); };
  }, []);

  useEffect(() => {
    if (effectiveLocationScope !== locationScope) {
      onLocationScopeChange?.(effectiveLocationScope);
    }
  }, [effectiveLocationScope, locationScope, onLocationScopeChange]);

  const itemCode = item.code || row?.itemCode || "-";
  const itemName = item.name || row?.itemName || t("Inventory item");
  const hierarchy = [
    item.family_name || row?.familyName,
    item.subfamily_name || row?.subfamilyName,
    item.category_name || row?.categoryName,
    item.subcategory_name || row?.subcategoryName,
  ]
    .filter((part) => part && part !== "-")
    .join(" / ");

  const selectedLocation = locationSummaries.find(
    (location) => location.scope_key === effectiveLocationScope
  );
  const locationProvenance = Array.isArray(locationLedger.provenance)
    ? locationLedger.provenance
    : [];
  const selectedProvenance = locationProvenance.find(
    (entry) => entry.scope_key === effectiveLocationScope
  );
  const initialLocationSelection =
    row?.initialLocationSelection?.scope_key === effectiveLocationScope
      ? row.initialLocationSelection
      : null;
  const selectableLocationSummaries = (
    initialLocationSelection && !selectedLocation
      ? [...locationSummaries, initialLocationSelection]
      : locationSummaries
  ).filter((location) => hasStockQuantity(location.current_qty));
  const isLegacyScope = effectiveLocationScope === "legacy_unallocated";
  const isLocationScope = effectiveLocationScope !== "global";
  const selectedScopeName = isLegacyScope
    ? t("Legacy / Unallocated")
    : selectedLocation?.name || initialLocationSelection?.name || t("All locations");
  const currentQty = isLegacyScope
    ? locationLedger.legacy_unallocated?.current_qty
    : selectedLocation
    ? selectedLocation.current_qty
    : initialLocationSelection
    ? initialLocationSelection.current_qty
    : summary.current_qty ?? row?.quantity;
  const currentValue = summary.current_value ?? row?.totalValue;
  const currentAvgCost = summary.current_avg_cost ?? row?.avgCost;
  const displayedMovements = useMemo(
    () =>
      isLocationScope
        ? locationEntries.filter((entry) => entry.location_scope_key === effectiveLocationScope)
        : movements,
    [effectiveLocationScope, isLocationScope, locationEntries, movements]
  );
  const movementCount =
    pagination.total_count !== null &&
    pagination.total_count !== undefined &&
    Number.isFinite(Number(pagination.total_count))
    ? Number(pagination.total_count)
    : isLocationScope
      ? displayedMovements.length
      : summary.movements_count ?? movements.length;
  const movementCountIsExact = pagination.total_is_exact !== false;
  const ledgerContextTruncated = Boolean(
    pagination.context_truncated || locationLedger.context_truncated
  );
  const unitOptions = buildDeliveryNoteUnitOptions(item, undefined, languageId).filter(
    (option) => Number.isFinite(option.factor) && option.factor > 0
  );
  const baseUnit = unitOptions.find((option) => option.level === "unit") || {
    level: "unit", factor: 1, label: item.unit_name || item.unit?.name || row?.unitName || "Unit not set",
  };
  const availableUnits = unitOptions.some((option) => option.level === "unit") ? unitOptions : [baseUnit];
  const displayUnit = availableUnits.find((option) => option.level === displayUnitLevel) || baseUnit;
  const unitName = t(displayUnit.label);
  const baseUnitName = t(baseUnit.label);
  const basis = inventoryBasis(item);
  const currentExact = isLegacyScope ? locationLedger.legacy_unallocated?.exact_quantity
    : isLocationScope ? (selectedLocation || initialLocationSelection)?.exact_quantity : summary.exact_quantity ?? row?.exactQuantity;
  const displayQuantity = (value, exact) => {
    if (value == null) return null;
    const physical = exact || historicalExact(value, basis);
    return quantityFromExact(physical, displayUnit.level, 4) ?? convertLedgerNumber(value, displayUnit.factor);
  };
  const quantityText = (value, exact) => value == null ? "-" : formatExactQuantity(exact, displayUnit.level, languageId) ?? formatOptionalDecimal(displayQuantity(value, exact));
  const signedQuantityText = (value, exact) => {
    const converted = displayQuantity(value, exact);
    return converted === null ? "-" : formatExactQuantity(exact, displayUnit.level, languageId, true) ?? formatSignedDecimal(converted);
  };
  const unitCostText = (value) => {
    const converted = convertLedgerNumber(value, 1 / displayUnit.factor);
    return converted === null ? "-" : formatUnitCost(converted);
  };
  const signedValueText = (value) => {
    const amount = convertLedgerNumber(value, 1);
    return amount === null ? "-" : formatSignedCurrency(amount);
  };
  const lastMovementAt = isLocationScope
    ? displayedMovements[0]?.created_at || null
    : summary.last_movement_at || row?.lastMovementAt;

  return (
    <div
      className="sm-modalOverlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="sm-ledgerModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sm-ledger-title"
        aria-busy={loading}
        tabIndex={-1}
      >
        <div className="sm-ledgerHeader">
          <div>
            <div className="sm-ledgerKicker">{t("Item Movement Ledger")}</div>
            <h2 id="sm-ledger-title" className="sm-ledgerTitle">
              {itemCode} - {itemName}
            </h2>
            {hierarchy ? <div className="sm-ledgerMeta">{hierarchy}</div> : null}
          </div>

          <div className="sm-ledgerHeaderActions">
            <button
              type="button"
              className="sm-actionBtn sm-ledgerClose"
              aria-label={t("Close item movement ledger")}
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </div>

        <div className="sm-ledgerBody">
          <div className="sm-ledgerStats">
            <div className="sm-stat">
              <div className="sm-statLabel">{t("Movements")}</div>
              <div className="sm-statValue">{loading ? "-" : movementCount}</div>
            </div>
            <div className="sm-stat">
              <div className="sm-statLabel">
                {isLocationScope
                  ? t("{0} Qty", { 0: selectedScopeName })
                  : hasRestrictedLocationAccess
                  ? t("Authorized Qty")
                  : t("Current Qty")}
              </div>
              <div className="sm-statValue">
                <InventoryQuantity formatted={currentQty == null ? null : formatExactQuantity(currentExact, displayUnit.level, languageId)}
                  unavailableTitle={currentQty != null && displayQuantity(currentQty, currentExact) == null ? t("Exact subunit balance unavailable") : undefined}
                  value={displayQuantity(currentQty, currentExact)} unitName={unitName} optional maximumFractionDigits={8} />
              </div>
            </div>
            <div className="sm-stat">
              <div className="sm-statLabel">
                {isLocationScope
                  ? globalTotalsVisible
                    ? t("Global Value")
                    : t("Authorized Value")
                  : hasRestrictedLocationAccess
                  ? t("Authorized Value")
                  : t("Current Value")}
              </div>
              <div className="sm-statValue">{formatOptionalCurrency(currentValue)}</div>
            </div>
            <div className="sm-stat">
              <div className="sm-statLabel">
                {hasRestrictedLocationAccess || (isLocationScope && !globalTotalsVisible)
                  ? t("Authorized Avg Price")
                  : t("Current Avg Price")}
              </div>
              <div className="sm-statValue">{unitCostText(currentAvgCost)} <small className="sm-ledgerCostUnit">/ {unitName}</small></div>
            </div>
            <div className="sm-stat">
              <div className="sm-statLabel">{t("Valuation")}</div>
              <div className="sm-statValue">{t(summary.valuation_method || "-")}</div>
            </div>
            <div className="sm-stat">
              <div className="sm-statLabel">
                {isLocationScope
                  ? t("Last Location Change")
                  : hasRestrictedLocationAccess
                  ? t("Last Authorized Movement")
                  : t("Last Movement")}
              </div>
              <div className="sm-statValue">{formatDate(lastMovementAt)}</div>
            </div>
          </div>

          {locationLedger.enabled ? (
            <div className="sm-ledgerScopeBar" role="group" aria-label={t("Movement location filter")}>
              <span className="sm-ledgerScopeLabel">{t("Location")}</span>
              <button
                type="button"
                className={`sm-ledgerScopeBtn ${effectiveLocationScope === "global" ? "sm-ledgerScopeBtn--active" : ""}`}
                aria-pressed={effectiveLocationScope === "global"}
                onClick={() => onLocationScopeChange?.("global")}
              >
                {t(globalScopeLabel)}
                <span className="sm-ledgerScopeQty">{quantityText(summary.current_qty, summary.exact_quantity)}</span>
              </button>
              {selectableLocationSummaries.map((location) => (
                <button
                  type="button"
                  key={location.scope_key}
                  className={`sm-ledgerScopeBtn ${effectiveLocationScope === location.scope_key ? "sm-ledgerScopeBtn--active" : ""}`}
                  aria-pressed={effectiveLocationScope === location.scope_key}
                  onClick={() => onLocationScopeChange?.(location.scope_key)}
                >
                  {location.name}
                  {location.is_active === false ? t(" (inactive)") : ""}
                  <span className="sm-ledgerScopeQty">{quantityText(location.current_qty, location.exact_quantity)}</span>
                </button>
              ))}
              {showLegacyScope ? (
                <button
                  type="button"
                  className={`sm-ledgerScopeBtn ${isLegacyScope ? "sm-ledgerScopeBtn--active" : ""}`}
                  aria-pressed={isLegacyScope}
                  onClick={() => onLocationScopeChange?.("legacy_unallocated")}
                >{t("Legacy / Unallocated")}<span className="sm-ledgerScopeQty">
                    {quantityText(locationLedger.legacy_unallocated?.current_qty, locationLedger.legacy_unallocated?.exact_quantity)}
                  </span>
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="sm-ledgerUnitBar" role="group" aria-label={t("Unit of measure")}>
            <span className="sm-ledgerScopeLabel">{t("Unit of measure")}</span>
            {availableUnits.map((option) => (
              <button
                key={option.level}
                type="button"
                className="sm-actionBtn sm-ledgerUnitBtn"
                aria-pressed={option.level === displayUnit.level}
                disabled={loading}
                onClick={() => onDisplayUnitChange(option.level)}
              >
                {t(option.label)}
              </button>
            ))}
            <span className="sm-ledgerUnitHint">
              {displayUnit.factor !== 1 ? `1 ${baseUnitName} = ${formatOptionalDecimal(displayUnit.factor)} ${unitName} · ` : ""}
              {t("Display only — stock records are unchanged.")}
            </span>
          </div>

          {ledgerContextTruncated ? (
            <div className="sm-ledgerContextNotice" role="status">
              {t("History is limited to the latest {0} supporting records. Older derived events may be missing, so reconciliation is inconclusive. All immutable movement pages remain available.", { 0: pagination.context_limit || locationLedger.context_limit || 5000 })}
            </div>
          ) : null}

          {isLocationScope && !isLegacyScope && selectedProvenance ? (
            <div className="sm-provenance" role="status">
              <div>
                <div className="sm-provenanceTitle">{t("How this balance is composed")}</div>
                <div className="sm-provenanceHint">
                  {t("Latest completed count in {0}, followed by movements attributed explicitly or through the Main Location fallback rule.", { 0: selectedScopeName })}
                </div>
              </div>
              <div>
                {selectedProvenance.anchor ? (
                  <div className="sm-provenanceFormula">
                    {selectedProvenance.anchor.anchor_type === "reconciliation"
                      ? `${selectedProvenance.anchor.reference_label || t("Location reconciliation")}: `
                      : t("Count #{0}: ", { 0: selectedProvenance.anchor.inventory_count_id })}
                    {formatOptionalDecimal(selectedProvenance.anchor.selected_quantity)} {selectedProvenance.anchor.selected_unit_name || t("selected units")}
                    {Number(selectedProvenance.anchor.conversion_factor || 1) !== 1
                      ? ` ÷ ${formatOptionalDecimal(selectedProvenance.anchor.conversion_factor)}`
                      : ""}
                    {` = ${formatOptionalDecimal(selectedProvenance.anchor.quantity)} ${baseUnitName}`}
                    {displayUnit.factor !== 1 ? ` = ${quantityText(selectedProvenance.anchor.quantity)} ${unitName}` : ""}
                    {t(" · later movements {0}", { 0: signedQuantityText(selectedProvenance.subsequent_delta) })}
                    {t(" · reconstructed ")}
                    <strong>{quantityText(selectedProvenance.explained_qty)} {unitName}</strong>
                  </div>
                ) : (
                  <div className="sm-provenanceFormula">{t("No completed location count is available as a starting point.")}</div>
                )}
                {selectedProvenance.events?.length ? (
                  <div className="sm-provenanceEvents">
                    {selectedProvenance.events.slice(0, 6).map((event, eventIndex) => (
                      <span
                        className="sm-provenanceEvent"
                        key={event.event_key || `${event.movement_id || "event"}-${eventIndex}`}
                      >
                        {formatDate(event.movement_date)} · {event.reference_label || t(getMovementTypeLabel(event.movement_type))} · {signedQuantityText(event.quantity_delta)}
                      </span>
                    ))}
                    {Number(selectedProvenance.events_count ?? selectedProvenance.events.length) > 6 ? (
                      <span className="sm-provenanceEvent">
                        +{Number(selectedProvenance.events_count ?? selectedProvenance.events.length) - 6} {t("more")}</span>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div
                className={`sm-provenanceStatus ${
                  selectedProvenance.status === "reconciled" || selectedProvenance.status === "empty"
                    ? ""
                    : selectedProvenance.status === "rounding_residual"
                      ? "sm-provenanceStatus--warning"
                      : "sm-provenanceStatus--bad"
                }`.trim()}
              >
                <div className="sm-provenanceStatusLabel">
                    {selectedProvenance.status === "reconciled" || selectedProvenance.status === "empty"
                      ? t("Explained")
                    : selectedProvenance.status === "inconclusive"
                      ? t("History window limited")
                    : selectedProvenance.status === "rounding_residual"
                      ? t("Rounding residue")
                      : selectedProvenance.status === "no_count_anchor"
                        ? t("No count anchor")
                        : t("Unexplained difference")}
                </div>
                <div className="sm-provenanceResult">{t("Current")} <strong>{quantityText(selectedProvenance.current_qty)} {unitName}</strong>
                  {selectedProvenance.anchor
                    ? t(" · difference {0}", { 0: signedQuantityText(selectedProvenance.unexplained_delta) })
                    : ""}
                </div>
              </div>
            </div>
          ) : null}

          {loading ? (
            <div className="sm-ledgerEmpty" role="status" aria-live="polite">{t("Loading item movements...")}</div>
          ) : error ? (
            <div className="sm-ledgerError" role="alert">
              <span>{error}</span>
              <button type="button" className="sm-actionBtn" onClick={onRetry}>{t("Retry")}</button>
            </div>
          ) : !displayedMovements.length ? (
            <div className="sm-ledgerEmpty">
              {t("No movements were found for {0}.", { 0: isLocationScope ? selectedScopeName : t("this item") })}
            </div>
          ) : (
            <section className="sm-ledgerSection">
              <div className="sm-ledgerSectionHead">
                <div className="sm-ledgerSectionTitle">
                  {isLocationScope
                    ? t("{0} Movement Ledger", { 0: selectedScopeName })
                    : t("{0} Movement Ledger", { 0: globalScopeLabel })}
                </div>
                <div className="sm-ledgerPageActions">
                  <span className="sm-ledgerPageStatus">
                    {movementCountIsExact
                      ? t("Showing {0} of {1}", { 0: displayedMovements.length, 1: movementCount })
                      : t("Showing {0} · at least {1} indexed", { 0: displayedMovements.length, 1: movementCount })}
                  </span>
                  {pagination.has_more ? (
                    <button
                      type="button"
                      className="sm-actionBtn sm-ledgerLoadMore"
                      disabled={loadingMore}
                      onClick={onLoadMore}
                    >
                      {loadingMore ? t("Loading…") : t("Load older")}
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="sm-ledgerTableWrap">
                <table
                  className={`sm-table sm-table--ledger ${
                    isLocationScope ? "sm-table--locationLedger" : "sm-table--globalLedger"
                  }`}
                  aria-label={
                    isLocationScope
                      ? t("{0} movement ledger", { 0: selectedScopeName })
                      : t("{0} movement ledger", { 0: globalScopeLabel })
                  }
                >
                  <thead>
                    <tr>
                      <th>{t("Movement Date")}</th>
                      <th>{t("Accounting Date")}</th>
                      <th>{t("Type")}</th>
                      <th>{t("Reference")}</th>
                      {isLocationScope ? (
                        <>
                          <th className="sm-num">{t("Before (")}{unitName})</th>
                          <th className="sm-num">{t("Delta (")}{unitName})</th>
                          <th className="sm-num">{t("After (")}{unitName})</th>
                          <th>{t("Role / Counterpart")}</th>
                        </>
                      ) : (
                        <>
                          <th className="sm-num">{t("Qty (")}{unitName})</th>
                          <th className="sm-num">{t("Move Avg")} <small className="sm-ledgerCostUnit">/ {unitName}</small></th>
                          <th className="sm-num">{t("Move Value")}</th>
                          <th className="sm-num">{t("Stock Qty (")}{unitName})</th>
                          <th className="sm-num">{t("Stock Value")}</th>
                          <th className="sm-num">{t("Stock Avg Price")} <small className="sm-ledgerCostUnit">/ {unitName}</small></th>
                        </>
                      )}
                      <th>{t("Recorded")}</th>
                      <th>{t("Notes")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedMovements.map((movement, index) => {
                      const movementTypeLabel = getMovementTypeLabel(movement.movement_type, movement);
                      const referenceLabel = getReferenceLabel(movement, t);
                      const target = getReferenceTarget(movement);
                      const referenceTarget = target && canOpenReference(target) ? target : null;
                      const locationRoleLabel = isLocationScope
                        ? getLocationRoleLabel(movement, locationEntries, t)
                        : "";

                      return (
                        <tr key={movement.id || movement.movement_id || index}>
                          <td className="sm-cellStrong">{formatDate(movement.movement_date || movement.created_at)}</td>
                          <td className="sm-cellStrong">
                            <span className="sm-accountingDates">
                              <span>{formatDate(movement.accounting_date || movement.movement_date)}</span>
                              {movement.is_corrected && movement.source_correction_posting_date ? (
                                <span
                                  className="sm-accountingDateCorrection"
                                  title={t("Correction accounting date")}
                                >
                                  {formatDate(movement.source_correction_posting_date)}
                                </span>
                              ) : null}
                            </span>
                          </td>
                          <td className="sm-typeCell" title={t(movementTypeLabel)}>
                            <span className="sm-moveTypeWrap">
                              <span className={`sm-pill sm-moveType ${getMovementToneClass(movement)}`}>
                                {t(movementTypeLabel)}
                              </span>
                              {movement.is_corrected && movement.movement_type === "purchase" ? (
                                <span
                                  className="sm-correctedBadge"
                                  title={movement.source_correction_posting_date ? t("Corrected on {0}", { 0: formatDate(movement.source_correction_posting_date) }) : t("Corrected")}
                                >{t("Corrected")}</span>
                              ) : null}
                            </span>
                          </td>
                          <td className="sm-reference" title={referenceLabel}>
                            {referenceTarget?.type === "stock_return" ? <a className="sm-referenceBtn" href={`/purchasing/stock-management/returns?${new URLSearchParams({ business_unit_id: String(referenceTarget.business_unit_id || businessUnitId), document_id: String(referenceTarget.id) })}`}>{referenceLabel}</a> : referenceTarget ? (
                              <button
                                type="button"
                                className="sm-referenceBtn"
                                onClick={() => onOpenReference?.(referenceTarget, movement)}
                              >
                                {referenceLabel}
                              </button>
                            ) : (
                              referenceLabel
                            )}
                          </td>
                          {isLocationScope ? (
                            <>
                              <td className="sm-num">
                                {quantityText(movement.location_qty_before, movement.before_exact_quantity)}
                              </td>
                              <td className={`sm-num ${getNumberToneClass(movement.quantity)}`}>
                                {signedQuantityText(movement.quantity, movement.exact_quantity)}
                              </td>
                              <td className="sm-num">
                                {quantityText(movement.location_qty_after, movement.location_exact_quantity_after || movement.stock_exact_quantity_after)}
                              </td>
                              <td title={locationRoleLabel}>
                                <span className="sm-locationRole">{locationRoleLabel}</span>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className={`sm-num ${getNumberToneClass(movement.quantity)}`}>
                                {signedQuantityText(movement.quantity, movement.exact_quantity)}
                              </td>
                              <td className="sm-num">{unitCostText(movement.unit_cost)}</td>
                              <td className={`sm-num ${getNumberToneClass(movement.total_cost)}`}>
                                {signedValueText(movement.total_cost)}
                              </td>
                              <td className="sm-num">{quantityText(movement.stock_qty_after, movement.stock_exact_quantity_after)}</td>
                              <td className="sm-num">{formatOptionalCurrency(movement.stock_value_after)}</td>
                              <td className="sm-num">{unitCostText(movement.stock_avg_cost_after)}</td>
                            </>
                          )}
                          <td className="sm-cellMuted">{formatDateTime(movement.created_at)}</td>
                          <td className="sm-cellMuted" title={movement.notes || ""}>
                            {truncateText(movement.notes || "-", 70)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// Key each ledger session to its item and business unit so late responses cannot
// appear in a different report row or hotel after either selection changes.
export default function ItemMovementHistory(props) {
  const { row, businessUnitId } = props;
  if (!row?.itemId || !businessUnitId) return null;
  return <ItemMovementHistorySession key={`${businessUnitId}:${row.itemId}:${row.initialLocationScope || "global"}`} {...props} />;
}

function ItemMovementHistorySession({ row, businessUnitId, onClose, onDataChanged }) {
  const { themeId, languageId } = usePmsTheme();
  const { t } = useMemo(() => inventoryPresentation(languageId), [languageId]);
  // Updating translations must not restart the selected location's ledger.
  const translateRef = useRef(t);
  translateRef.current = t;
  const { can, requestConfig, contextKey } = usePurchasingReadAccess(businessUnitId);
  const ledgerPermission = "purchasing.stock.current.movements";
  const initialScope = row.initialLocationScope || "global";
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [locationScope, setLocationScope] = useState(initialScope);
  const [displayUnitLevel, setDisplayUnitLevel] = useState("unit");
  const [referenceModal, setReferenceModal] = useState(null);
  const requestRef = useRef(0);

  const loadLedger = useCallback(async ({ clearExisting = false, scope = "global", cursor = null, append = false } = {}) => {
    if (!can(ledgerPermission, true)) return;
    const requestToken = ++requestRef.current;
    if (clearExisting) setLedger(null);
    setError("");
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const response = await axios.get(`/api/purchasing/inventory/item-ledger/${row.itemId}`, {
        ...requestConfig(ledgerPermission),
        params: { business_unit_id: businessUnitId, ...itemLedgerRequestParams({ scope, cursor }) },
      });
      if (requestRef.current !== requestToken || !can(ledgerPermission, true)) return;
      setLedger((current) => mergeItemLedgerPage(current, response.data || { movements: [] }, { append }));
    } catch (loadError) {
      if (requestRef.current !== requestToken || !can(ledgerPermission, true)) return;
      console.error("[STOCK][ITEM-LEDGER][ERROR]", loadError);
      setError(loadError.response?.data?.error || translateRef.current("Failed to load item movement ledger."));
    } finally {
      if (requestRef.current === requestToken) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [businessUnitId, row.itemId, can, requestConfig]);

  useEffect(() => {
    setLedger(null); setReferenceModal(null);
    loadLedger({ clearExisting: true, scope: initialScope });
    return () => { requestRef.current += 1; };
  }, [initialScope, loadLedger, contextKey]);

  const closeLedger = useCallback(() => {
    requestRef.current += 1;
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (referenceModal) return undefined;
    const handleKeyDown = (event) => { if (event.key === "Escape") closeLedger(); };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeLedger, referenceModal]);

  const changeLocationScope = useCallback((scope) => {
    const nextScope = scope || "global";
    setLocationScope(nextScope);
    loadLedger({ clearExisting: true, scope: nextScope });
  }, [loadLedger]);

  const canOpenReference = useCallback((target) => {
    if (!can(ledgerPermission, true) || (target.business_unit_id && Number(target.business_unit_id) !== Number(businessUnitId))) return false;
    if (target.type === "inventory_count") return can("purchasing.stock.inventory_count", true) || can("purchasing.stock.inventory_count.view_all", true);
    return can(target.type === "stock_return" ? "purchasing.stock.returns" : "purchasing.delivery_notes.view_tab", true);
  }, [businessUnitId, can]);

  const openReference = useCallback((target) => {
    if (!canOpenReference(target)) return;
    const id = Number(target?.id);
    if (!Number.isInteger(id) || id <= 0 || !["delivery_note", "inventory_count"].includes(target?.type)) return;
    setReferenceModal({ type: target.type, id });
  }, [canOpenReference]);

  const closeReference = useCallback(() => {
    setReferenceModal(null);
    loadLedger({ scope: locationScope });
    onDataChanged?.();
  }, [loadLedger, locationScope, onDataChanged]);

  const loadMore = () => {
    const cursor = ledger?.pagination?.next_cursor;
    if (!cursor || loading || loadingMore) return;
    loadLedger({ scope: locationScope, cursor, append: true });
  };

  if (!can(ledgerPermission)) return <div role="alert">{t("You don't have permission to access this section.")}<button onClick={onClose}>{t("Close")}</button></div>;

  return (
    <div className="pilcore-purchasing-ui inventory-intelligence" data-theme={themeId}>
      {!referenceModal ? (
        <InventoryItemMovementsModal
          businessUnitId={businessUnitId}
          row={row}
          ledger={ledger}
          loading={loading}
          loadingMore={loadingMore}
          error={error}
          locationScope={locationScope}
          onLocationScopeChange={changeLocationScope}
          onClose={closeLedger}
          onOpenReference={openReference}
          canOpenReference={canOpenReference}
          onRetry={() => loadLedger({ scope: locationScope })}
          onLoadMore={loadMore}
          displayUnitLevel={displayUnitLevel}
          onDisplayUnitChange={setDisplayUnitLevel}
        />
      ) : null}
      {referenceModal?.type === "delivery_note" ? (
        <EditDeliveryNotePopup isOpen deliveryNoteId={referenceModal.id} onClose={closeReference} />
      ) : null}
      {referenceModal?.type === "inventory_count" ? (
        <AddInventoryCountPopup isOpen pilcoreAppearance sessionIdFromList={referenceModal.id} businessUnitId={businessUnitId} onClose={closeReference} />
      ) : null}
    </div>
  );
}
